/**
 * PacmanMazeScreen - Main Pac-Man game screen
 */

import { useEffect, useMemo, useCallback, useRef } from 'react';
import { useGame } from '../context/GameContext';
import { usePacmanGame } from '../hooks/usePacmanGame';
import { useFireworksCode } from '../hooks/useFireworksCode';
import { useAudio } from '../hooks/useAudio';
import { Maze, CapturePopup, FireworksOverlay } from '../components/pacman';
import './PacmanMazeScreen.css';

export function PacmanMazeScreen() {
  const { state: gameState, actions } = useGame();
  const { members, pacmanCaptureOrder, pacmanMaze } = gameState;
  const initializedRef = useRef(false);
  const { play, stop, stopAll } = useAudio();

  // Use ALL members for maze generation
  const allMemberIds = useMemo(
    () => members.map(m => m.accountId),
    [members]
  );

  const pacman = usePacmanGame(allMemberIds);

  // Initialize game once on mount
  useEffect(() => {
    if (allMemberIds.length > 0 && !initializedRef.current) {
      initializedRef.current = true;
      // Use saved maze or generate new one
      const maze = pacman.initGame(pacmanMaze);
      // Save newly generated maze
      if (!pacmanMaze && maze) {
        actions.setPacmanMaze(maze);
      }
      // Sync captured ghosts and auto-start if returning from Kanban
      if (pacmanCaptureOrder.length > 0) {
        pacman.syncCapturedGhosts(pacmanCaptureOrder);
        // Auto-start chase after a short delay (to let state update)
        setTimeout(() => {
          pacman.startChase();
        }, 500);
      }
    }
    // Note: pacman, pacmanMaze, pacmanCaptureOrder, and actions are intentionally
    // excluded from deps - we only want to run this once when members are loaded
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allMemberIds.length]);

  // Handle phase transitions
  useEffect(() => {
    if (pacman.state.phase === 'TRANSITION') {
      // Find the captured member and set as current
      const capturedMember = members.find(
        m => m.accountId === pacman.state.capturedMemberId
      );
      if (capturedMember) {
        actions.selectMember(capturedMember);
        actions.addPacmanCapture(capturedMember.accountId);
      }
      actions.setPhase('PACMAN_KANBAN');
    }
  }, [pacman.state.phase, pacman.state.capturedMemberId, members, actions]);

  // Sound effects based on Pac-Man game phase
  useEffect(() => {
    if (pacman.state.phase === 'CHASE') {
      play('pacman-chase', { loop: true, volume: 0.4 });
    } else if (pacman.state.phase === 'POPUP') {
      stop('pacman-chase');
      play('pacman-capture', { volume: 0.6 });
    } else if (pacman.state.phase === 'IDLE' || pacman.state.phase === 'TRANSITION') {
      stop('pacman-chase');
    }
  }, [pacman.state.phase, play, stop]);

  // Stop all sounds when leaving the screen
  useEffect(() => {
    return () => stopAll();
  }, [stopAll]);

  // Handle maze regeneration
  const handleRegenerateMaze = useCallback(() => {
    const maze = pacman.initGame(null); // Force new maze
    if (maze) {
      actions.setPacmanMaze(maze);
    }
    actions.resetPacmanCaptures();
  }, [pacman, actions]);

  // Keyboard controls
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.code === 'Tab') {
        e.preventDefault();
        actions.setPhase('RECAP');
      } else if (e.code === 'Space' && pacman.state.phase === 'IDLE') {
        pacman.startChase();
      } else if (e.code === 'KeyR' && pacman.state.phase === 'IDLE') {
        handleRegenerateMaze();
      } else if (e.code === 'Escape') {
        actions.setPhase('GAME_MODE_SELECT');
      }
    };

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [pacman.state.phase, actions, handleRegenerateMaze]);

  // Fireworks easter egg (only on last ghost)
  const handleFireworks = useCallback(() => {
    if (pacman.state.isLastGhost && pacman.state.phase === 'POPUP') {
      pacman.triggerFireworks();
    }
  }, [pacman.state.isLastGhost, pacman.state.phase]);

  useFireworksCode(handleFireworks, pacman.state.isLastGhost);

  // Find captured member for popup
  const capturedMember = useMemo(() => {
    if (!pacman.state.capturedMemberId) return null;
    return members.find(m => m.accountId === pacman.state.capturedMemberId) || null;
  }, [pacman.state.capturedMemberId, members]);

  const activeGhostCount = pacman.state.ghosts.filter(g => !g.isCaptured).length;

  return (
    <div className="pacman-screen">
      <header className="pacman-screen__header">
        <span className="pacman-screen__title">DAILY CHASE</span>
        <span className="pacman-screen__counter">{activeGhostCount} ghosts</span>
      </header>

      <div className="pacman-screen__game">
        {pacman.state.maze && pacman.state.pacman && (
          <Maze
            maze={pacman.state.maze}
            pacman={pacman.state.pacman}
            ghosts={pacman.state.ghosts}
          />
        )}
      </div>

      <footer className="pacman-screen__footer">
        {pacman.state.phase === 'IDLE' && (
          <span className="pacman-screen__hint">
            [SPACE] Start &nbsp; [R] Regenerate &nbsp; [TAB] Recap
          </span>
        )}
        {pacman.state.phase === 'CHASE' && (
          <span className="pacman-screen__hint pacman-screen__hint--active">
            Chase in progress...
          </span>
        )}
      </footer>

      {pacman.state.phase === 'POPUP' && capturedMember && (
        <CapturePopup
          member={capturedMember}
          isLast={pacman.state.isLastGhost}
          onDone={pacman.goToKanban}
        />
      )}

      {pacman.state.fireworksTriggered && <FireworksOverlay />}
    </div>
  );
}
